import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Analytics } from '@vercel/analytics/react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Elements } from '@stripe/react-stripe-js';
import { ErrorBoundary } from './components/ErrorBoundary';
import { NavigationErrorBoundary } from './components/NavigationErrorBoundary';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { AuthProvider } from '@/contexts/AuthContext';
import { AppErrorBoundary } from '@/components/AppErrorBoundary';
import { initializePerformanceMonitoring } from '@/lib/performance';
import AdminHardRedirect from '@/components/AdminHardRedirect';

// Import critical pages (loaded immediately)
import Landing from './pages/Landing';
import HealthcareProfessionals from './pages/HealthcareProfessionals';
import Employers from './pages/Employers';
import AuthCallback from './pages/AuthCallback';
import PostAuth from './pages/PostAuth';
import DebugAuth from './pages/DebugAuth';
import SupabaseTest from './pages/SupabaseTest';
import Vision from './pages/Vision';
import GenericErrorPage from './pages/GenericErrorPage';
import SimpleTest from './pages/SimpleTest';
import TestButton from './pages/TestButton';
import SimpleReactButton from './pages/SimpleReactButton';
import Privacy from './pages/Privacy';
import DashboardRedirect from './pages/DashboardRedirect';
import NotFound from './pages/NotFound';

// Import lazy routes
import { LazyRoutes } from './routes/LazyRoutes';

// Import components
import ProtectedRoute from './components/ProtectedRoute';
import ProtectedRouteNew from './components/ProtectedRouteNew';
import AdminRoute from './components/AdminRoute';
import DashboardAccessGuard from './routes/DashboardAccessGuard';
import { DashboardErrorBoundary } from './components/DashboardErrorBoundary';
import AdminLayout from './components/AdminLayout';
import NewNavigationGuard from './routes/NavigationGuard';
import ScrollToTop from './components/ScrollToTop';
import ScrollToTopButton from './components/ScrollToTopButton';
import AnalyticsRouteTracker from './components/AnalyticsRouteTracker';
import { stripePromise } from '@/lib/stripe';
import ComingSoon from './pages/ComingSoon';

// Query client configuration with optimized caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000, // 5 minutes - data stays fresh longer
      gcTime: 15 * 60_000, // 15 minutes - keep in cache longer
      retry: (count, error: any) => {
        // Don't retry on 401/403 errors (auth issues)
        if (error?.status === 401 || error?.status === 403) return false;
        // Retry up to 2 times for other errors
        return count < 2;
      },
      refetchOnWindowFocus: false, // Prevent unnecessary refetches
      refetchOnReconnect: true, // Refetch when connection restored
      refetchOnMount: true, // Always refetch on mount for fresh data
    },
    mutations: {
      retry: (count, error: any) => {
        // Don't retry mutations on auth errors
        if (error?.status === 401 || error?.status === 403) return false;
        return count < 1; // Retry once for other errors
      },
    },
  },
});

// Root Layout Component
function RootLayout() {
  return (
    <>
      <ScrollToTop />
      <NewNavigationGuard />
      <AdminHardRedirect />
      <Routes>
        {/* PUBLIC ROUTES */}
        <Route index element={<Landing />} />
        <Route path="coming-soon" element={<ComingSoon />} />
        <Route path="healthcare-professionals" element={<HealthcareProfessionals />} />
        <Route path="employers" element={<Employers />} />
        <Route path="candidate-signup" element={<ComingSoon />} />
        <Route path="candidate/signin" element={<ComingSoon />} />
        <Route path="employer/signup" element={<ComingSoon />} />
        <Route path="employer/signin" element={<ComingSoon />} />
        <Route path="auth/callback" element={<AuthCallback />} />
        <Route path="post-auth" element={<PostAuth />} />
        <Route path="password-setup" element={<ComingSoon />} />
        {import.meta.env.DEV && <Route path="debug-auth" element={<DebugAuth />} />}
        <Route path="supabase-test" element={<SupabaseTest />} />
        <Route path="vision" element={<Vision />} />
        {/* Role selection for OAuth users */}
        <Route path="select-role" element={<ComingSoon />} />
        <Route path="role-select" element={<Navigate to="/coming-soon" replace />} />
        <Route path="role-selection" element={<Navigate to="/coming-soon" replace />} />
        <Route path="candidate-signin" element={<Navigate to="/coming-soon" replace />} />
        <Route path="signin" element={<Navigate to="/coming-soon" replace />} />
        <Route path="login" element={<ComingSoon />} />
        <Route path="error" element={<GenericErrorPage />} />
        <Route path="choose-role" element={<ComingSoon />} />
        <Route path="auth" element={<ComingSoon />} />
        <Route path="auth/subdomain" element={<ComingSoon />} />
        <Route path="auth/test" element={<ComingSoon />} />
        <Route path="auth/oauth-test" element={<ComingSoon />} />
        <Route path="oauth-test" element={<ComingSoon />} />
        <Route path="test" element={<SimpleTest />} />
        <Route path="test-button" element={<TestButton />} />
        <Route path="simple-react-button" element={<SimpleReactButton />} />
        <Route path="password-reset" element={<ComingSoon />} />
        <Route path="privacy" element={<Privacy />} />
        <Route path="dashboard-redirect" element={<DashboardRedirect />} />
        
        {/* LEGACY ROUTE REDIRECTS */}
        <Route path="employer-signin" element={<Navigate to="/coming-soon" replace />} />
        <Route path="employer-signup" element={<Navigate to="/coming-soon" replace />} />
        <Route path="employer-sign-up" element={<Navigate to="/coming-soon" replace />} />
        <Route path="employers/signup" element={<Navigate to="/coming-soon" replace />} />
        <Route path="candidate-signin" element={<Navigate to="/coming-soon" replace />} />
        
        {/* LAZY LOADED ROUTES - Dashboard and admin pages */}
        {...LazyRoutes()}
        
        {/* Back-compat redirects (handle /admin/dashboard and any subpaths) */}
        <Route path="/admin/dashboard" element={<Navigate to="/admin/overview" replace />} />
        <Route path="/admin/dashboard/*" element={<Navigate to="/admin/overview" replace />} />
        
        {/* Fix for underscore vs hyphen in routes */}
        <Route path="/job_seeker/dashboard" element={<Navigate to="/job-seeker/dashboard" replace />} />
        <Route path="/job_seeker/*" element={<Navigate to="/job-seeker/dashboard" replace />} />
        
        <Route path="review" element={<ProtectedRoute><ComingSoon /></ProtectedRoute>} />
        
        {/* CATCH ALL */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

function App() {
  // Initialize performance monitoring
  React.useEffect(() => {
    initializePerformanceMonitoring();
  }, []);
  const onVercelProd = (import.meta.env.PROD && (import.meta.env.VERCEL === '1' || !!import.meta.env.VERCEL_ENV));

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ErrorBoundary>
          <NavigationErrorBoundary>
            <Elements stripe={stripePromise}>
              <Toaster />
              <Sonner />
              {import.meta.env.PROD && <AnalyticsRouteTracker />}
              <RootLayout />
              <ScrollToTopButton />
              {onVercelProd && <Analytics />}
            </Elements>
          </NavigationErrorBoundary>
        </ErrorBoundary>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
